import { Link2, Lock } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { MinimumStayWithTarget, PriceWithTarget } from '../automation/AutomationMarks';
import type { AutomationCell } from '../automation/automationModel';
import type { DayCellModel } from '../calendar/calendarModel';

/** Werte eines freien Tags im Monat: Sperre, Verbund oder Preis und Mindestnächte mit Ziel. */
export function MonthCellValues({
  cell,
  currency,
  automation,
  compact = false,
}: {
  cell: DayCellModel;
  currency: string;
  automation: AutomationCell | null;
  compact?: boolean;
}) {
  const { day } = cell;
  if (compact) return <CompactValue cell={cell} automation={automation} />;
  if (cell.kind === 'blocked') {
    return (
      <span className="text-ink-soft text-xs font-bold">
        {day.stopSell ? 'Verkaufsstopp' : 'gesperrt'}
      </span>
    );
  }
  if (cell.kind === 'closedByFamily') {
    return (
      <span className="text-ink-soft flex items-center gap-1 text-xs font-semibold">
        <Link2 size={13} aria-hidden /> Verbund belegt
      </span>
    );
  }
  return (
    <span className="flex flex-col items-start">
      <PriceWithTarget
        rate={day.rate}
        currency={currency}
        manual={day.rateSource === 'Manual'}
        prefill={day.rateSource === 'Prefill'}
        target={automation?.targetRate}
        className="text-[17px]"
      />
      <MinimumStayWithTarget
        value={day.minStay}
        manual={day.minimumStaySource === 'Manual'}
        target={automation?.targetMinimumStay}
        className="text-[11px]"
      />
    </span>
  );
}

/** Handy (Board „Verfügbarkeit · Handy“): nur der Preis – mit Ziel „→118“ – oder das Zeichen. */
function CompactValue({
  cell,
  automation,
}: {
  cell: DayCellModel;
  automation: AutomationCell | null;
}) {
  const { day } = cell;
  if (cell.kind === 'blocked')
    return <span className="text-ink-soft text-[10px] font-bold">gesperrt</span>;
  if (cell.kind === 'closedByFamily')
    return <Link2 size={12} aria-hidden className="text-ink-faint" />;
  const target = automation?.targetRate;
  return (
    <span
      className={cn(
        'flex items-center gap-0.5 whitespace-nowrap text-xs font-bold tabular-nums',
        target != null
          ? 'text-amber-ink'
          : day.rateSource === 'Prefill'
            ? 'text-ink-soft'
            : 'text-ink',
      )}
    >
      {target != null ? `→${target}` : (day.rate ?? '—')}
      {day.rateSource === 'Manual' && <Lock size={9} strokeWidth={2.6} aria-hidden />}
    </span>
  );
}
